import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// TermsOfService.js — professional dark mode layout matching PrivacyPolicy/About
// ─────────────────────────────────────────────────────────────────────────────

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

export default function TermsOfService() {
  useEffect(() => {
    document.title = 'Flap Card - Terms of Service';
  }, []);

  return (
    <section className="flap-terms min-h-screen bg-[#06112b] text-white relative overflow-hidden">
      {/* Global styles — consistent with PrivacyPolicy & Testimonial */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        @import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

        .flap-terms {
          padding: 120px 5% 100px;
          position: relative;
          font-family: 'DM Sans', sans-serif;
        }

        .flap-terms::before {
          content: '';
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(28,115,186,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(28,115,186,0.05) 1px, transparent 1px);
          background-size: 52px 52px;
          pointer-events: none;
        }

        .flap-terms::after {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(28,115,186,0.12) 0%, transparent 70%);
          top: -150px; right: -150px;
          pointer-events: none;
        }

        .flap-terms-inner {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .terms-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: #f5a623;
          margin-bottom: 16px;
          text-align: center;
        }

        .terms-heading {
          font-family: 'Clash Display', sans-serif;
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 700;
          text-align: center;
          line-height: 1.15;
          margin-bottom: 60px;
          color: #fff;
        }

        .terms-heading span {
          color: #1c73ba;
        }

        .terms-date {
          text-align: center;
          font-size: 1rem;
          color: rgba(10, 2, 100, 0.6);
          margin-bottom: 48px;
        }

        .terms-content {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 48px 32px;
          backdrop-filter: blur(12px);
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        }

        .terms-section-title {
          font-family: 'Clash Display', sans-serif;
          font-size: 2.2rem;
          font-weight: 600;
          color: #0f0229;
          margin: 64px 0 24px;
        }

        .terms-section-title:first-child {
          margin-top: 0;
        }

        .terms-text {
          color: rgba(3, 2, 21, 0.85);
          line-height: 1.8;
          font-size: 1.05rem;
          margin-bottom: 24px;
        }

        .terms-list {
          list-style: disc;
          padding-left: 32px;
          margin-bottom: 32px;
        }

        .terms-list li {
          margin-bottom: 16px;
          color: rgba(137, 3, 6, 0.9);
          font-size: 1.05rem;
          line-height: 1.7;
        }

        .terms-link {
          color: #5aaef7;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .terms-link:hover {
          color: #1c73ba;
        }

        .terms-subsection-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: #ffffff;
          margin: 32px 0 16px;
        }

        @media (max-width: 768px) {
          .terms-heading { font-size: clamp(2.2rem, 7vw, 3.8rem); }
          .terms-content { padding: 32px 24px; }
        }
      `}</style>

      <div className="flap-terms-inner">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.span className="terms-label" variants={fadeInUp}>
            Legal Agreement
          </motion.span>

          <motion.h1 className="terms-heading" variants={fadeInUp}>
            Terms of <span>Service</span>
          </motion.h1>

          <motion.p className="terms-date" variants={fadeInUp}>
            Effective Date: June 01, 2025
          </motion.p>

          <motion.div className="terms-content" variants={fadeInUp}>
            <p className="terms-text">
              Welcome to Flap Card, a digital contact-sharing platform operated by <a href="https://www.flap.com.np" className="terms-link">Flap Tech Pvt Ltd</a> ("Flap Card," "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our Software Applications, website, and related services (collectively, the "Services").
            </p>

            <p className="terms-text">
              By accessing, registering for, or using the Services, you ("User," "you," or "your") agree to be bound by these Terms. If you do not agree, please do not use our Services.
            </p>

            <h2 className="terms-section-title">1. Eligibility</h2>
            <p className="terms-text">
              You must be at least 16 years old to use the Services. By using the Services, you represent that you meet this age requirement and have the legal capacity to enter into these Terms.
            </p>

            <h2 className="terms-section-title">2. Account Registration</h2>
            <p className="terms-text">
              To use certain features, you must create an account. You agree to provide accurate, current, and complete information during registration and to update it as necessary. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>

            <h2 className="terms-section-title">3. Acceptable Use</h2>
            <p className="terms-text">
              You agree not to:
            </p>
            <ul className="terms-list">
              <li>Use the Services for any unlawful or unauthorized purpose</li>
              <li>Interfere with or disrupt the Services or servers</li>
              <li>Upload viruses, malware, or harmful code</li>
              <li>Harass, abuse, or harm others</li>
              <li>Impersonate any person or entity</li>
              <li>Violate any applicable laws or third-party rights</li>
            </ul>

            <h2 className="terms-section-title">4. User Content & Responsibilities</h2>
            <p className="terms-text">
              You retain ownership of the content you upload or share through the Services. By using the Services, you grant Flap Card a worldwide, non-exclusive, royalty-free license to host, display, and distribute your content solely to provide the Services.
            </p>
            <p className="terms-text">
              You are solely responsible for the accuracy, legality, and appropriateness of the information you share via your Flap Card.
            </p>

            <h2 className="terms-section-title">5. Flap Card Sharing</h2>
            <p className="terms-text">
              When you share your Flap Card, recipients receive the information you choose to make public. Be aware that recipients may forward or share your card further (e.g., within their organization). Flap Card is not responsible for how third parties use shared information.
            </p>

            <h2 className="terms-section-title">6. Intellectual Property</h2>
            <p className="terms-text">
              The Services, including software, design, text, graphics, and logos, are owned by Flap Tech Pvt Ltd or our licensors and protected by copyright, trademark, and other laws. You may not copy, modify, distribute, or create derivative works without our written permission.
            </p>

            <h2 className="terms-section-title">7. Termination</h2>
            <p className="terms-text">
              We may suspend or terminate your access to the Services at any time, without notice, for any reason, including violation of these Terms. Upon termination, your right to use the Services ends immediately.
            </p>

            <h2 className="terms-section-title">8. Limitation of Liability</h2>
            <p className="terms-text">
              To the fullest extent permitted by law, Flap Card is not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services.
            </p>

            <h2 className="terms-section-title">9. Indemnification</h2>
            <p className="terms-text">
              You agree to indemnify and hold Flap Card harmless from any claims, losses, liabilities, damages, or expenses arising from your use of the Services or violation of these Terms.
            </p>

            <h2 className="terms-section-title">10. Changes to Terms</h2>
            <p className="terms-text">
              We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on our website or via email (if applicable). Continued use of the Services after changes constitutes acceptance.
            </p>

            <h2 className="terms-section-title">11. Governing Law & Dispute Resolution</h2>
            <p className="terms-text">
              These Terms are governed by the laws of Nepal. Any disputes shall be resolved exclusively in the courts of Kathmandu, Nepal.
            </p>

            <h2 className="terms-section-title">12. Contact Us</h2>
            <p className="terms-text">
              For questions about these Terms, contact us at:<br />
              Email: <a href="mailto:legal@flap.com.np" className="terms-link">legal@flap.com.np</a><br />
              Address: Lazimpat-2, Kathmandu, Nepal
            </p>

            <p className="mt-12 text-center text-gray-400 text-sm">
              Thank you for using Flap Card.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}